import * as React from 'react';
import cn from 'classnames';
import styles from './ChangeTab.module.scss';
import {
    AnimatedTabs, Card, Details, renderRow, SmsSignModal, StatusColumn, Textarea, WarningModal,
} from 'components';
import { Button, Tab } from 'react-bootstrap';
import {
    ExpertChangeRequestStatus,
    MappedExpertChangeRequestStatus,
    MappedExpertChangeRequestType
} from 'components/enums';
import { Color, ColorStatus, formatPhoneNumber } from '_helpers';
import { ReactNodeValue } from 'components/Details';
import { StepStatus } from 'components/StepStatus';
import { ExpertChangeRequestDetails, IApiError, IExpert } from '../../../api/ApiClient';
import { useLoading } from 'hooks';
import { Dispatch, IRootState } from '../../../redux/store/configureStore';
import { connect } from 'react-redux';

const InfoModal: React.FC<IProps> = (props) => {
    const [showSendSms, setShowSendSms] = React.useState(false);
    const [showReject, setShowReject] = React.useState(false);
    const [rejectReason, setRejectReason] = React.useState<string>('');


    const [sendSmsProcessing, sendCode] = useLoading(async (captcha?: string) => props.sendCodeForApproveExpertChangeRequest({ expertChangeRequestId: props.details.id, body: { captcha } }));
    const [approveExpertChangeRequestProcessing, approveExpertChangeRequest] = useLoading(async (code: string, captcha?: string) => props.approveExpertChangeRequest({ expertChangeRequestId: props.details.id, body: { captcha, code } }));
    const [rejectExpertChangeRequestProcessing, rejectExpertChangeRequest] = useLoading(async (code: string, captcha?: string) => props.rejectExpertChangeRequest({ expertChangeRequestId: props.details.id, body: { captcha, code, rejectReason } }), [rejectReason]);


    const handleSubmitCode = (code: string, captcha?: string) => {
        approveExpertChangeRequest(code, captcha).then((success) => {
            setShowSendSms(!success);
            if (success) {
                props.onHide();
            }
        });
    };

    const handleRejectCode = (code: string, captcha?: string) => {
        rejectExpertChangeRequest(code, captcha).then((success) => {
            setShowSendSms(!success);
            if (success) {
                props.onHide();
            }
        });
    };

    const handleHideSmsModal = () => {
        setShowSendSms(false);
        setRejectReason('');
    };

    const handleHideRejectModal = () => {
        setShowReject(false);
        setRejectReason('');
    };

    const handleStartToRejectExpertChangeRequest = () => {
        sendCode();
        setShowReject(false);
        setShowSendSms(true);
    };

    const handleStartToApproveExpertChangeRequest = () => {
        sendCode();
        setShowSendSms(true);
    };

    const getStatus = (isRejected: boolean, isSuccess: boolean) => {
        if (isRejected) {
            return 'error';
        }

        if (isSuccess) {
            return 'success';
        }

        return 'loading';
    };

    const infoHeaderButtons = () => (
      <div className="d-flex">
          <Button className="mr-40" onClick={handleStartToApproveExpertChangeRequest}>
              Утвердить запрос
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowReject(true)}>
              Отклонить
          </Button>
      </div>
    );

    return (
      <WarningModal
        dialogClassName={cn('p-0', styles.p0Child)}
        titleClassNames={styles.buttonMargin}
        bodyClassNames="p-0"
        title={(props.details && props.details.fullName) || ''}
        withoutTitleMargin
        withoutTitlePadding
        withoutCancelButton
        show={props.show}
        onHide={props.onHide}
        large
      >
          <AnimatedTabs>
              <Tab title="Информация" eventKey="info">
                  <Card
                    title="Информация о запросе"
                    category="Общая информация о запросе на изменение"
                    withoutBorder
                    showLoader={props.fetchExpertChangeRequestProcessing}
                    headerChild={props.details.approvalProgress.find(ap => !ap.expertId) && infoHeaderButtons()}
                  >
                      {props.details && (
                        <section>

                            {renderRow('ДАТА СОЗДАНИЯ', props.details.dateTime ? props.details.dateTime.format('DD.MM.YYYY') : '')}
                            {renderRow('ТИП ЗАПРОСА', props.details.requestType ? MappedExpertChangeRequestType[props.details.requestType].text : '')}
                            {props.details.newExpertStatus && renderRow('НОВАЯ РОЛЬ', props.details.newExpertStatus)}
                            {renderRow(
                              'СТАТУС ЗАПРОСА',
                              <StatusColumn
                                status={props.details.status ? MappedExpertChangeRequestStatus[props.details.status] : new ColorStatus(Color.Default, '')}
                              />,
                            )}
                            {props.details.status  === ExpertChangeRequestStatus.Rejected && renderRow('ПРИЧИНА', <i>{props.details.rejectReason}</i>)}

                            {renderRow('ФАМИЛИЯ', props.details.expert.lastName, 'mt-5')}
                            {renderRow('ИМЯ', props.details.expert.firstName)}
                            {renderRow('ОТЧЕСТВО', props.details.expert.middleName)}

                            {renderRow('ДАТА РОЖДЕНИЯ', props.details.expert.birthDate.format('DD.MM.YYYY'), 'mt-5')}
                            {renderRow('РЕГИОН', props.details.expert.region)}

                            {renderRow('ТЕЛЕФОН', formatPhoneNumber(props.details.expert.phone), 'mt-5')}
                            {renderRow('ПОЧТА', props.details.expert.email)}

                            {renderRow('МЕСТО РАБОТЫ', props.details.expert.placeOfWork, 'mt-5')}
                            {renderRow('ДОЛЖНОСТЬ', props.details.expert.position)}

                        </section>
                      )}
                  </Card>
              </Tab>
              <Tab title="Процесс утверждения" eventKey="process">
                  <Card
                    title="Процесс утверждения запроса"
                    category="Процесс утверждения запроса на изменение"
                    withoutBorder
                    showLoader={props.fetchExpertChangeRequestProcessing}
                  >
                      <Details
                          centeredRow
                          withDarkRowLabel
                          infoRows={props.details ? props.details.approvalProgress.map((step, index) => {
                              if(!props.details) return new ReactNodeValue('','');
                              const prevStepCompletionDateTime = index > 0 ? props.details.approvalProgress[index - 1].completionDateTime : null;
                              const lastCompleted = (index === props.details.approvalProgress.length - 1 || props.details.approvalProgress[index + 1].completionDateTime == null) && step.completionDateTime != null;
                              return new ReactNodeValue(
                                  step.completionDateTime
                                      ? step.completionDateTime.format('DD.MM.YYYY - HH:mm')
                                      : prevStepCompletionDateTime ? 'Ожидание' : '',
                                  <StepStatus
                                      step={{
                                          value: step.value,
                                          label: step.label,
                                          status: getStatus(props.details.status === 'rejected' && lastCompleted, !!step.completionDateTime),
                                      }}
                                  />,
                              )
                          }).slice().reverse() : []}
                      />
                  </Card>
              </Tab>
          </AnimatedTabs>
          <SmsSignModal
            submitButtonText={rejectReason ? 'Отклонить запрос на изменение' : 'Утвердить'}
            onHide={handleHideSmsModal}
            show={showSendSms}
            phone={props.profile.phone}
            apiError={props.apiError}
            waitingSeconds={props.smsStatus && props.smsStatus.waitingSeconds}
            processing={sendSmsProcessing || (rejectReason ? rejectExpertChangeRequestProcessing : approveExpertChangeRequestProcessing)}
            onSendPasswordAgain={sendCode}
            onSubmit={rejectReason ? handleRejectCode : handleSubmitCode}
            title="Утверждение запроса на изменение"
            phoneText="Для утверждения запроса на изменение, введите код из СМС-сообщения, которое было отправлено на номер "
          />

          <WarningModal
            title="Отклонение запроса на изменение"
            show={showReject}
            onHide={handleHideRejectModal}
            accessBtnTittle="Отклонить запрос на изменение"
            onAccess={handleStartToRejectExpertChangeRequest}
          >
              <p className="mb-50">
                  Опишите причину отклонения запроса, чтобы другие члены комиссии могли ознакомиться с этой информацией.
              </p>

              <Textarea
                value={rejectReason}
                name="rejectReason"
                onChange={setRejectReason}
                rows={6}
              />
          </WarningModal>
      </WarningModal>
    );
};

interface OwnProps {
    show: boolean;
    onHide: () => void;
    details: ExpertChangeRequestDetails;
    profile: IExpert;
    fetchExpertChangeRequestProcessing?: boolean;
    apiError?: IApiError | null;
}

const mapStateToProps = (state: IRootState) => ({
    smsStatus: state.expertChanges.smsStatus,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    sendCodeForApproveExpertChangeRequest: dispatch.expertChanges.sendCodeForApproveExpertChangeRequest,
    approveExpertChangeRequest: dispatch.expertChanges.approveExpertChangeRequestAsync,
    rejectExpertChangeRequest: dispatch.expertChanges.rejectExpertChangeRequest,
});

type ConnectedProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;
type IProps = ConnectedProps & OwnProps;

export default connect(mapStateToProps, mapDispatchToProps)(InfoModal);
